import React, {useContext, useEffect, useRef, useState} from "react";
import "react-bootstrap-carousel/dist/react-bootstrap-carousel.css";
import Swal from "sweetalert2";
import {ReactComponent as IconeDominio} from "../../assets/newImages/compartilhar/dominio.svg";
import {ReactComponent as IconeGuia} from "../../assets/newImages/compartilhar/icone_guia.svg";
import {ReactComponent as LinkIcon} from "../../assets/newImages/icons/link-h.svg";
import {ReactComponent as Mail} from "../../assets/newImages/icons/mail.svg";
import {ReactComponent as Telegram} from "../../assets/newImages/icons/telegram-alt.svg";
import {ReactComponent as WeChat} from "../../assets/newImages/icons/wechat.svg";
import {ReactComponent as WhatsApp} from "../../assets/newImages/icons/whatsapp.svg";
import Form, {FormRef} from "../../components/Form/index";
import {Select} from "../../components/Select/index";
import SkeletonCompartilhar from "../../components/Skeletons/SkeletonCompartilhar/SkeletonCompartilhar";
import {AuthContext} from "../../contexts/Auth/AuthContext";
import {EmpresaContext} from "../../contexts/Empresa/EmpresaContext";
import {useTheme} from "../../contexts/Theme/ThemeContext";
import {CatalogosApi} from "../../hooks/catalogos";
import {PERMISSOES} from "../../services/Consts";
import {staginEnv} from "../../services/Functions";
import ConfigurarDominio from "./ConfigurarDominio";
import LinkAgrupamento from "./LinkAgrupamento/LinkAgrupamento";
import {
    ContainerCompartilhar,
    IconsContainer,
    IconWrapper,
    Middle,
} from "./styles";

interface CompartilharProps {
}

export const Compartilhar: React.FC<CompartilharProps> = ({}) => {
    const {theme} = useTheme();

    const catalogosApi = CatalogosApi();
    const {permissoesUsuario} = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [categorias, setCategorias] = useState<any>([]);
    const [categoriaSelecionada, setCategoriaSelecionada] = useState<any>("");
    const [loadingCategorias, setLoadingCategorias] = useState(false);
    const [catalogos, setCatalogos] = useState<any>([]);
    const [catalogoSelecionado, setCatalogoSelecionado] = useState<any>("");
    const [copiandoLink, setCopiandoLink] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const formRef = useRef<FormRef | null>(null);

    const {empresa, setEmpresa} = useContext(EmpresaContext);
    const mensagemWpp = encodeURIComponent(empresa?.mensagem_whats ?? "Olá, veja o catálogo da minha empresa:");

    const init = async () => {
        try {
            setLoading(true);
            // Wait for both catalogos and permissions to be loaded
            const catalogos = await catalogosApi.getCatalogosCompartilhar();
            
            if (!empresa || !permissoesUsuario) {
                throw new Error('Waiting for empresa or permissions to load');
            }

            setCatalogos(
                catalogos.map((catalogo: any) => {
                    return {
                        value: catalogo.identificacao,
                        name: catalogo.descricao,
                    };
                }),
            );
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            // Only remove loading state if we have all required data
            if (empresa && permissoesUsuario) {
                setLoading(false);
            }
        }
    };

    const icons = [
        {
            component: LinkIcon,
            label: `${copiandoLink ? "Link copiado" : "Link"}`,
            funcao: () => {
                navigator.clipboard
                    .writeText(`${empresa?.dominio_https}${catalogoSelecionado}${categoriaSelecionada}`)
                    .then(
                        () => {
                            setCopiandoLink(true);
                            setTimeout(() => {
                                setCopiandoLink(false);
                            }, 2000);
                        },
                        (err) => {
                            Swal.fire({
                                icon: "error",
                                title: "Erro ao copiar link",
                                text: `Mas você ainda pode tentar copiar o link manualmente! \n ${empresa?.dominio_https}${catalogoSelecionado}`,
                            });
                        },
                    );
            },
        },
        {
            component: WhatsApp,
            label: "WhatsApp",
            funcao: () => {
                window.open(
                    `https://api.whatsapp.com/send?text=${mensagemWpp}${empresa?.dominio_https}${catalogoSelecionado}${categoriaSelecionada}`,
                );
            },
        },
        {
            component: Telegram,
            label: "Telegram",
            funcao: () => {
                window.open(
                    `https://telegram.me/share/url?url=${empresa?.dominio_https}${catalogoSelecionado}&text=${mensagemWpp}${categoriaSelecionada}`,
                );
            },
        },
        {
            component: Mail,
            label: "E-mail",
            funcao: () => {
                window.open(
                    `mailto:?subject=${mensagemWpp}&body=${empresa?.dominio_https}${catalogoSelecionado}${categoriaSelecionada}`,
                );
            },
        },
        {
            component: WeChat,
            label: "WeChat",
            funcao: () => {
                window.open(
                    `https://www.addtoany.com/add_to/wechat?linkurl=${empresa?.dominio_https}${catalogoSelecionado}${categoriaSelecionada}`,
                );
            },
        },
    ];

    const buscarCategorias = async () => {
        setLoadingCategorias(true);
        const pastas = await catalogosApi.getCategorias(catalogoSelecionado);

        // Agrupa por categoria pai e monta um select só com "Categoria / Subcategoria",
        // igual ao do produto. O value de cada opção já é o sufixo pronto da URL.
        const mapa = new Map<string, Set<string>>();
        pastas.forEach((pasta: any) => {
            const temPai = Number(pasta.id_pasta_pai) > 0 && pasta.pasta_pai;
            const categoria = temPai ? pasta.pasta_pai : pasta.descricao;
            if (!mapa.has(categoria)) mapa.set(categoria, new Set());
            if (temPai) mapa.get(categoria)!.add(pasta.descricao);
        });

        const opcoes: { value: string; name: string }[] = [
            { value: "", name: "Todas as categorias" },
        ];
        Array.from(mapa.keys())
            .sort((a, b) => a.localeCompare(b))
            .forEach((categoria) => {
                opcoes.push({
                    value: `?categoria=${encodeURIComponent(categoria)}`,
                    name: categoria,
                });
                Array.from(mapa.get(categoria)!)
                    .sort((a, b) => a.localeCompare(b))
                    .forEach((sub) => {
                        opcoes.push({
                            value: `?categoria=${encodeURIComponent(categoria)}&subcategoria=${encodeURIComponent(sub)}`,
                            name: `${categoria} / ${sub}`,
                        });
                    });
            });

        setCategorias(opcoes);
        setCategoriaSelecionada("");
        setLoadingCategorias(false);
    }

    useEffect(() => {
        init();
    }, [empresa, permissoesUsuario]);

    useEffect(() => {
        if (catalogoSelecionado) {
            buscarCategorias();
        }
    }, [catalogoSelecionado]);

    return (
        <>
            <ContainerCompartilhar theme={theme} isGrid={true}>
                {loading ? (
                    <SkeletonCompartilhar/>
                ) : (
                    <>
                        <div className="conteudo">
                            <div className="topo">
                                <IconeGuia/>
                                <div className="texto fonteTitulo">
                                    Compartilhe seu catálogo <br/>
                                    com seus clientes
                                </div>
                            </div>
                            <div className="catalogos">
                                <div className="titulo fonteSubtitulo">Catálogos</div>
                                <Form id={""} setFormValid={setIsValid} ref={formRef}>
                                    <Select
                                        sizeLabel="medium"
                                        name="categoria"
                                        value={catalogoSelecionado}
                                        onChange={(newValue) => {
                                            setCatalogoSelecionado(newValue);
                                        }}
                                        placeholder="Selecione um catálogo"
                                        options={catalogos}
                                        required={true}
                                    />
                                </Form>
                                <div className="titulo fonteSubtitulo">Categorias</div>
                                <Select
                                    disabled={!catalogoSelecionado}
                                    sizeLabel="medium"
                                    name="categoria"
                                    loading={loadingCategorias}
                                    value={categoriaSelecionada}
                                    onChange={(newValue) => {
                                        setCategoriaSelecionada(newValue);
                                    }}
                                    placeholder="Todas as categorias"
                                    options={categorias}
                                />
                            </div>
                            <Middle theme={theme}>
                                <div></div>
                            </Middle>
                            <div className="compartilhar">
                                <div className="titulo fonteSubtitulo">Compartilhe via</div>
                                <IconsContainer theme={theme}>
                                    {icons.map((icon, index) => (
                                        <IconWrapper
                                            key={index}
                                            firstTwo={index < 2}
                                            theme={theme}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                formRef.current && formRef.current.submitForm();
                                                isValid && icon.funcao();
                                            }}
                                        >
                                            <div className="bola">
                                                <icon.component/>
                                            </div>
                                            <div className="texto fonteRotulos">{icon.label}</div>
                                        </IconWrapper>
                                    ))}
                                </IconsContainer>
                            </div>
                            {permissoesUsuario.includes(PERMISSOES.CONFIGURACOES) && (
                                <ConfigurarDominio dominio={empresa?.dominio_https} empresa={empresa}/>
                            )}
                        </div>
                        <div className="lado-direito">
                            <LinkAgrupamento/>
                        </div>
                    </>
                )}
            </ContainerCompartilhar>
        </>
    );
};

export default Compartilhar;
